import { Filter, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import { useAuditEvents } from "@/api/hooks";
import type { AdminAuditEventResponse } from "@/api/types";

/** Audit log viewer for PencilOut staff.
 *
 *  Reads ``/admin/audit-events`` (newest-first). Supports filtering by
 *  action substring or target id (org UUID). Each row expands inline
 *  to show its parsed ``payload_json``.
 *
 *  Why not just `tail -f` the box: this surfaces things tied to a
 *  specific org or action without grepping logs that mix every
 *  request. Plus it survives container restarts (rows live in
 *  Postgres, not the container's filesystem). */
// Common target types we surface in the dropdown — stays in sync
// with whatever services/audit.py emits. Adding a new value to the
// dropdown is purely cosmetic; the server filter accepts any string.
const TARGET_TYPES = [
  "organization",
  "user",
  "client",
  "job",
  "quote",
  "walkthrough",
  "app_config",
  "plan_version",
  "service_token",
  "billing",
];

export function AuditLogPage() {
  const [actionFilter, setActionFilter] = useState("");
  const [actionPrefixFilter, setActionPrefixFilter] = useState("");
  const [targetIdFilter, setTargetIdFilter] = useState("");
  const [targetTypeFilter, setTargetTypeFilter] = useState("");
  const [actorEmailFilter, setActorEmailFilter] = useState("");
  // ISO date strings from <input type="datetime-local">. We need
  // to suffix them with seconds + the local timezone to send a
  // valid ISO-8601 to the server (FastAPI's datetime parser is
  // strict). Build these on submit.
  const [sinceLocal, setSinceLocal] = useState("");
  const [untilLocal, setUntilLocal] = useState("");
  const [limit, setLimit] = useState(100);

  const events = useAuditEvents({
    action: actionFilter || undefined,
    action_prefix: actionPrefixFilter || undefined,
    target_id: targetIdFilter || undefined,
    target_type: targetTypeFilter || undefined,
    actor_email: actorEmailFilter || undefined,
    since: sinceLocal ? new Date(sinceLocal).toISOString() : undefined,
    until: untilLocal ? new Date(untilLocal).toISOString() : undefined,
    limit,
  });

  function clearFilters() {
    setActionFilter("");
    setActionPrefixFilter("");
    setTargetIdFilter("");
    setTargetTypeFilter("");
    setActorEmailFilter("");
    setSinceLocal("");
    setUntilLocal("");
  }

  const hasActiveFilter = Boolean(
    actionFilter ||
      actionPrefixFilter ||
      targetIdFilter ||
      targetTypeFilter ||
      actorEmailFilter ||
      sinceLocal ||
      untilLocal,
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Audit log</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Append-only record of admin actions — impersonation, plan changes, billing edits, token
          mints. Newest first. The full payload is shown when you expand a row.
        </p>
      </div>

      {/* Filters */}
      <div className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
        {/* Row 1: action + prefix */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <span className="label-mono mb-1 block">Action (exact)</span>
            <div className="relative">
              <Filter
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
                aria-hidden
              />
              <input
                type="text"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                placeholder="e.g. plan.change_requested"
                className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[180px]">
            <span className="label-mono mb-1 block">Action prefix</span>
            <div className="relative">
              <Filter
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
                aria-hidden
              />
              <input
                type="text"
                value={actionPrefixFilter}
                onChange={(e) => setActionPrefixFilter(e.target.value)}
                placeholder="organization."
                className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Row 2: target type + target id + actor */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <span className="label-mono mb-1 block">Target type</span>
            <select
              value={targetTypeFilter}
              onChange={(e) => setTargetTypeFilter(e.target.value)}
              className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
            >
              <option value="">— any —</option>
              {TARGET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <span className="label-mono mb-1 block">Target ID</span>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
                aria-hidden
              />
              <input
                type="text"
                value={targetIdFilter}
                onChange={(e) => setTargetIdFilter(e.target.value)}
                placeholder="resource UUID"
                className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[180px]">
            <span className="label-mono mb-1 block">Actor email</span>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
                aria-hidden
              />
              <input
                type="text"
                value={actorEmailFilter}
                onChange={(e) => setActorEmailFilter(e.target.value)}
                placeholder="brad@…"
                className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Row 3: time window + limit + actions */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <span className="label-mono mb-1 block">Since</span>
            <input
              type="datetime-local"
              value={sinceLocal}
              onChange={(e) => setSinceLocal(e.target.value)}
              className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <span className="label-mono mb-1 block">Until</span>
            <input
              type="datetime-local"
              value={untilLocal}
              onChange={(e) => setUntilLocal(e.target.value)}
              className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
            />
          </div>
          <div className="w-24">
            <span className="label-mono mb-1 block">Limit</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs text-graphite-soft transition hover:bg-paper-warm hover:text-graphite"
            >
              Clear filters
            </button>
          )}
          <button
            type="button"
            onClick={() => events.refetch()}
            disabled={events.isFetching}
            className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-40"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${events.isFetching ? "animate-spin" : ""}`}
              aria-hidden
            />
            Refresh
          </button>
        </div>
      </div>

      {events.isLoading && <p className="text-sm text-graphite-soft">Loading events…</p>}
      {events.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {events.error instanceof ApiError ? events.error.detail : "Could not load events."}
        </p>
      )}
      {events.data && events.data.length === 0 && (
        <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
          No events match this filter.
        </p>
      )}

      {events.data && events.data.length > 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm">
          <div className="border-b border-rule px-4 py-2 text-xs text-graphite-soft">
            <span className="label-mono">{events.data.length} events</span>
          </div>
          <ul>
            {events.data.map((evt) => (
              <AuditEventRow key={evt.id} event={evt} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AuditEventRow({ event }: { event: AdminAuditEventResponse }) {
  const [open, setOpen] = useState(false);
  const parsedPayload = parsePayload(event.payload_json);

  return (
    <li className="border-b border-rule/60 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-paper"
      >
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-graphite-soft" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-sm font-semibold tabular-nums">{event.action}</span>
            <span className="font-mono text-xs text-graphite-soft tabular-nums">
              {new Date(event.created_at).toLocaleString()}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-xs text-graphite-soft">
            <span>by {event.actor_email ?? "—"}</span>
            {event.target_type && (
              <span>
                on {event.target_type}
                {event.target_id && ` ${event.target_id.slice(0, 8)}…`}
              </span>
            )}
          </div>
        </div>
        <span
          className={`mt-1 font-mono text-[10px] uppercase tracking-wider text-graphite-faint transition ${open ? "rotate-90" : ""}`}
          aria-hidden
        >
          ▶
        </span>
      </button>
      {open && parsedPayload !== null && (
        <pre className="overflow-x-auto border-t border-rule/40 bg-paper px-4 py-3 font-mono text-[11px] text-graphite-soft">
          {JSON.stringify(parsedPayload, null, 2)}
        </pre>
      )}
      {open && parsedPayload === null && (
        <p className="border-t border-rule/40 bg-paper px-4 py-3 font-mono text-[11px] text-graphite-faint">
          No payload.
        </p>
      )}
    </li>
  );
}

function parsePayload(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}
